import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { createSparePartType } from "@/integrations/supabase/inventory-services";
import { useAuth } from "@/context/AuthContext";

interface AddSparePartTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddSparePartTypeModal = ({ isOpen, onClose, onSuccess }: AddSparePartTypeModalProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const { profile } = useAuth();
  const { toast } = useToast();

  const createMutation = useMutation({
    mutationFn: ({ name, description, createdBy }: {
      name: string;
      description?: string;
      createdBy: string;
    }) => createSparePartType({
      name,
      description: description || null,
      created_by: createdBy
    }),
    onSuccess: () => {
      toast({
        title: "¡Éxito!",
        description: "Tipo de repuesto creado correctamente.",
      });
      onSuccess();
      handleClose();
    },
    onError: (error: unknown) => {
      console.error("Error creating spare part type:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al crear el tipo de repuesto.";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !profile?.id) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "El nombre es requerido.",
      });
      return;
    }

    createMutation.mutate({
      name: name.trim(),
      description: description.trim(),
      createdBy: profile.id
    });
  };

  const handleClose = () => {
    setName("");
    setDescription("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuevo Tipo de Repuesto</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre del Repuesto</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Llanta 26 pulgadas"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción (Opcional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción detallada del repuesto..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Creando..." : "Crear"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSparePartTypeModal;
